import { create } from 'zustand';
import {
  RCMember,
  RCMessage,
  RCPresenceStatus,
  RCRoom,
  RCTimestamp,
} from '../types/rocketchat.types';
import { RocketChatRESTService } from '../services/RocketChatRESTService';
import { parseTs } from '../utils/parseTs';

export interface InAppNotification {
  title: string;
  body: string;
  roomId: string;
}

export interface RCCurrentUser {
  _id: string;
  username: string;
  name: string;
}

interface ChatPanelState {
  isConnected: boolean;
  isLoggedIn: boolean;
  isDdpDead: boolean;
  currentUser: RCCurrentUser | null;

  rooms: Record<string, RCRoom>;
  roomOrder: string[];
  // True while the open room's message list is scrolled away from the latest
  // message — new messages then count as unread instead of being auto-read.
  activeRoomScrolledUp: boolean;
  // Message the active room's MessageList should scroll to (set by search), then clear.
  jumpTo: { rid: string; id: string } | null;
  // Rooms the user hid (subscription open: false). Kept out of the sidebar but
  // searchable, so they can be found and reopened like RC's own client allows.
  hiddenRooms: Record<string, RCRoom>;
  // Per-room member list, keyed by roomId. Absent key = not yet fetched (distinct
  // from an empty array, which means "fetched, zero other members").
  members: Record<string, RCMember[]>;

  messages: Record<string, RCMessage[]>;
  // Unix-ms timestamp of when messages were cleared; own messages before this time are hidden at render.
  clearedAt: Record<string, number>;
  // Personal Delete: every message before this time is hidden for this user only.
  deletedAt: Record<string, number>;
  loadingMessages: Record<string, boolean>;
  hasMoreMessages: Record<string, boolean>;

  activeRoomId: string | null;
  // The AI Assistant (NGE-1251) is open in place of a room. Kept apart from activeRoomId
  // because it isn't a Rocket.Chat room: room-list syncs drop unknown activeRoomIds.
  aiActive: boolean;
  // One-shot: set alongside clearActiveRoom() so RocketChatPanel's "restore last
  // room" mount effect (which only treats a truthy activeRoomId as an already-set
  // intent) knows to stay empty instead of repopulating the previous DM.
  suppressRoomRestore: boolean;
  editingMessage: { id: string; roomId: string; text: string } | null;
  deleteConfirm: { msgId: string; roomId: string } | null;
  replyingTo: { msgId: string; roomId: string; text: string; username: string } | null;
  quotingMessage: {
    msgId: string;
    roomId: string;
    text: string;
    username: string;
    name: string;
    ts: RCTimestamp;
    permalink: string;
  } | null;
  forwardingMessage: {
    msgId: string;
    text: string;
    senderUsername: string;
    senderName: string;
    ts: string;
    originRoomId: string;
  } | null;
  activeThread: { msgId: string; roomId: string; msg: string } | null;
  createDiscussionContext: { msgId: string; roomId: string; msg: string } | null;
  // Bumped after a discussion is created under a parent room so DiscussionsPanel
  // (which only fetches on mount) knows to re-fetch while it's already open.
  discussionsVersion: Record<string, number>;
  activeSidePanel:
    | 'user-info'
    | 'members'
    | 'threads'
    | 'discussions'
    | 'search'
    | 'files'
    | 'pinned'
    | 'starred'
    | 'notifications'
    | null;
  keyboardShortcutsOpen: boolean;
  filePreview: { file: File; onUpdate: (file: File) => void } | null;
  showDirectory: boolean;
  // Set for guest sessions to the guest's 1:1 room with their visit host. That room
  // is now superseded by the shared meeting-room chat (see ensureMeetingChatRoom /
  // FloorMap.tsx's occupancy effect) and is deliberately hidden — setRooms/upsertRoom
  // drop it — so the guest only ever sees the meeting room, not this redundant DM.
  // Other employees' own legitimate DMs to the guest are unaffected, only this one
  // specific room id is excluded. Still drives hiding the directory/search/create-new
  // UI (guests can't browse for new people).
  guestRestrictedRoomId: string | null;
  presence: Record<string, RCPresenceStatus>;
  typingUsers: Record<string, string[]>; // roomId -> usernames currently typing
  totalUnread: number;
  panelPrefs: { width: number; height: number } | null;
  inAppNotification: InAppNotification | null;
  drafts: Record<string, string>;
  fileDrafts: Record<string, File>;

  setConnected: (v: boolean) => void;
  setLoggedIn: (v: boolean) => void;
  setDdpDead: (v: boolean) => void;
  setRooms: (rooms: RCRoom[]) => void;
  upsertRoom: (room: Partial<RCRoom> & { _id: string }) => void;
  removeRoom: (roomId: string) => void;
  setActiveRoomScrolledUp: (v: boolean) => void;
  setJumpTo: (target: { rid: string; id: string } | null) => void;
  setHiddenRooms: (rooms: RCRoom[]) => void;
  // Moves a room from the sidebar into hiddenRooms (no-op if it isn't listed).
  hideRoom: (roomId: string) => void;
  // Puts a hidden room back in the sidebar (the caller reopens it in RC).
  unhideRoom: (roomId: string) => void;
  // Hides all of the room's history for this user and hides the room; a new message brings it back.
  deleteRoomForMe: (roomId: string) => void;
  setRoomMembers: (roomId: string, members: RCMember[]) => void;
  addRoomMember: (roomId: string, member: RCMember) => void;
  removeRoomMember: (roomId: string, userId: string) => void;
  // Drops the cached member list for a room (e.g. after a membership system
  // message we can't safely apply as a precise delta) so the next open re-fetches.
  invalidateRoomMembers: (roomId: string) => void;
  setActiveRoom: (roomId: string | null) => void;
  setAiActive: (active: boolean) => void;
  setSuppressRoomRestore: (v: boolean) => void;
  // Clears the active room and marks the restore effect to stay empty on this
  // mount, for "open chat with no room selected" cases (e.g. Start Chat found no
  // resolvable RC user) — plain setActiveRoom(null) alone would still get
  // overwritten by the persisted lastRoomId once RocketChatPanel mounts.
  clearActiveRoom: () => void;
  prependMessages: (roomId: string, msgs: RCMessage[]) => void;
  appendMessage: (roomId: string, msg: RCMessage) => void;
  updateMessage: (roomId: string, msg: RCMessage) => void;
  toggleReaction: (roomId: string, messageId: string, emoji: string, username: string) => void;
  replaceMessage: (roomId: string, oldId: string, newMsg: RCMessage) => void;
  removeMessage: (roomId: string, msgId: string) => void;
  clearRoomMessages: (roomId: string) => void; // records clearedAt timestamp; filtering happens at render
  purgeRoomMessages: (roomId: string) => void; // empties the local array so loadRoomHistory will re-fetch
  markRoomRead: (roomId: string) => void;
  markRoomUnread: (roomId: string) => void;
  // Optimistically drops a single thread from a room's unread-thread list (e.g. on opening it)
  clearThreadUnread: (roomId: string, msgId: string) => void;
  markAllRoomsRead: () => void;
  reconcileUnreadCounts: (counts: Record<string, number>) => void;
  toggleFavorite: (roomId: string, favorite: boolean) => void;
  setEditingMessage: (msg: { id: string; roomId: string; text: string } | null) => void;
  setDeleteConfirm: (confirm: { msgId: string; roomId: string } | null) => void;
  deleteAllConfirmOpen: boolean;
  setDeleteAllConfirmOpen: (open: boolean) => void;
  setReplyingTo: (
    reply: { msgId: string; roomId: string; text: string; username: string } | null
  ) => void;
  setQuotingMessage: (quote: ChatPanelState['quotingMessage']) => void;
  setForwardingMessage: (fwd: ChatPanelState['forwardingMessage']) => void;
  setActiveSidePanel: (panel: ChatPanelState['activeSidePanel']) => void;
  setKeyboardShortcutsOpen: (open: boolean) => void;
  setFilePreview: (preview: ChatPanelState['filePreview']) => void;
  setActiveThread: (thread: ChatPanelState['activeThread']) => void;
  setCreateDiscussionContext: (ctx: ChatPanelState['createDiscussionContext']) => void;
  bumpDiscussionsVersion: (parentRoomId: string) => void;
  setShowDirectory: (show: boolean) => void;
  setGuestRestrictedRoomId: (roomId: string | null) => void;
  setPresence: (userId: string, status: string) => void;
  setTyping: (roomId: string, username: string, isTyping: boolean) => void;
  setPanelPrefs: (prefs: { width: number; height: number }) => void;
  pushNotification: (n: InAppNotification) => void;
  clearNotification: () => void;
  setCurrentUser: (u: RCCurrentUser) => void;
  setDraft: (key: string, text: string) => void;
  clearDraft: (key: string) => void;
  setFileDraft: (key: string, file: File) => void;
  clearFileDraft: (key: string) => void;
  loadRoomHistory: (roomId: string, roomType: string, signal?: AbortSignal) => Promise<void>;
  loadMoreHistory: (roomId: string) => Promise<void>;
}

// RCTimestamp can be an ISO string or a Mongo { $date } object — normalise to string for comparisons.
const toSortKey = (ts: RCMessage['ts'] | undefined): string => {
  if (!ts) return '';
  if (typeof ts === 'string') return ts;
  return new Date(ts.$date).toISOString();
};

const roomSortKey = (room: Partial<RCRoom>): string => toSortKey(room.lastMessage?.ts);

// RC enriches messages asynchronously after the initial save (e.g. resolving a
// quote-link into an `attachments` block, oembed link previews) and pushes the
// enriched version over DDP — which can land before OR after our own REST
// send response for the same message. Both paths call updateMessage/
// replaceMessage, so a naive full-object replace means whichever arrives last
// wins and can silently wipe out fields the other one already had. Merge
// instead: keep the incoming message's fields, but don't let an
// attachment-less echo erase attachments we already know about.
//
// `reactions` needs the same treatment: a DDP echo for one reaction (server
// state as of that action) can land after a local optimistic toggle for a
// DIFFERENT emoji on the same message. A full-object replace would drop
// whichever emoji key the other side doesn't happen to know about yet, so we
// union the per-emoji keys instead of letting one side's snapshot win outright.
const mergeMessage = (existing: RCMessage | undefined, incoming: RCMessage): RCMessage => {
  if (!existing) return incoming;
  return {
    ...existing,
    ...incoming,
    attachments: incoming.attachments?.length ? incoming.attachments : existing.attachments,
    reactions:
      existing.reactions || incoming.reactions
        ? { ...existing.reactions, ...incoming.reactions }
        : incoming.reactions,
  };
};

// Deduplicate by _id, keeping the last occurrence (most recent version wins).
const dedupById = (msgs: RCMessage[]): RCMessage[] => {
  const seen = new Map<string, number>();
  msgs.forEach((m, i) => seen.set(m._id, i));
  return msgs.filter((m, i) => seen.get(m._id) === i);
};

// Full sort used only on initial load (setRooms). O(n log n) is acceptable there.
const sortedOrder = (rooms: Record<string, RCRoom>): string[] =>
  Object.values(rooms)
    .sort((a, b) => roomSortKey(b).localeCompare(roomSortKey(a)))
    .map((r) => r._id);

const fullTotalUnread = (rooms: Record<string, RCRoom>): number =>
  Object.values(rooms).reduce((sum, r) => sum + (r.unread || 0), 0);

// Insert `id` (with sort key `key`) into a descending-sorted order array,
// removing any existing entry for `id` first. O(n) for the filter + O(log n) for the search.
const insertInOrder = (
  order: string[],
  id: string,
  key: string,
  rooms: Record<string, RCRoom>
): string[] => {
  const filtered = order.filter((x) => x !== id);
  let lo = 0;
  let hi = filtered.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (roomSortKey(rooms[filtered[mid]]) > key) lo = mid + 1;
    else hi = mid;
  }
  return [...filtered.slice(0, lo), id, ...filtered.slice(lo)];
};

const CLEARED_KEY = 'rc-cleared-at';

const loadClearedAt = (): Record<string, number> => {
  try {
    const raw = JSON.parse(localStorage.getItem(CLEARED_KEY) ?? '{}');
    // Migrate old boolean entries (pre-timestamp approach) — treat as epoch 0 (show everything)
    const result: Record<string, number> = {};
    for (const [k, v] of Object.entries(raw)) {
      result[k] = typeof v === 'number' ? v : 0;
    }
    return result;
  } catch {
    return {};
  }
};

const saveClearedAt = (clearedAt: Record<string, number>) => {
  try {
    localStorage.setItem(CLEARED_KEY, JSON.stringify(clearedAt));
  } catch {}
};

// ponytail: delete-for-me lives in this browser only — move to a backend table if
// they need to follow the user across devices.
const DELETED_KEY = 'rc-deleted-at';

const loadTsMap = (key: string): Record<string, number> => {
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? '{}');
    return typeof raw === 'object' && raw !== null ? (raw as Record<string, number>) : {};
  } catch {
    return {};
  }
};

const saveTsMap = (key: string, map: Record<string, number>) => {
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {}
};

const DRAFTS_KEY = 'rc-message-drafts';

const loadDrafts = (): Record<string, string> => {
  try {
    const raw = JSON.parse(localStorage.getItem(DRAFTS_KEY) ?? '{}');
    return typeof raw === 'object' && raw !== null ? (raw as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const saveDrafts = (drafts: Record<string, string>) => {
  try {
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
  } catch {}
};

const lastMessageMs = (room: RCRoom): number => parseTs(room.lastMessage?.ts)?.getTime() ?? 0;

// Deleted-for-me rooms stay hidden in RC but aren't "archived" to the user, so the archive list skips them.
export const isDeletedForMe = (room: RCRoom, deletedAt: Record<string, number>) =>
  (deletedAt[room._id] ?? 0) >= lastMessageMs(room) && !!deletedAt[room._id];

export const useChatPanelStore = create<ChatPanelState>((set, get) => ({
  isConnected: false,
  isLoggedIn: false,
  isDdpDead: false,
  currentUser: null,
  rooms: {},
  roomOrder: [],
  activeRoomScrolledUp: false,
  jumpTo: null,
  hiddenRooms: {},
  members: {},
  messages: {},
  clearedAt: loadClearedAt(),
  deletedAt: loadTsMap(DELETED_KEY),
  loadingMessages: {},
  hasMoreMessages: {},
  activeRoomId: null,
  aiActive: false,
  suppressRoomRestore: false,
  editingMessage: null,
  deleteConfirm: null,
  deleteAllConfirmOpen: false,
  replyingTo: null,
  quotingMessage: null,
  forwardingMessage: null,
  activeThread: null,
  createDiscussionContext: null,
  discussionsVersion: {},
  activeSidePanel: null,
  keyboardShortcutsOpen: false,
  filePreview: null,
  showDirectory: false,
  guestRestrictedRoomId: null,
  presence: {},
  typingUsers: {},
  totalUnread: 0,
  panelPrefs: null,
  inAppNotification: null,
  drafts: loadDrafts(),
  fileDrafts: {},

  setConnected: (v) => set({ isConnected: v }),
  setLoggedIn: (v) => set({ isLoggedIn: v }),
  setDdpDead: (v) => set({ isDdpDead: v }),

  setRooms: (list) =>
    set((s) => {
      const filtered = s.guestRestrictedRoomId
        ? list.filter((r) => r._id !== s.guestRestrictedRoomId)
        : list;
      const rooms: Record<string, RCRoom> = {};
      for (const r of filtered) rooms[r._id] = r;
      // App-provisioned utility rooms (meeting-room chats, Auditorium room
      // chats — see ensureMeetingChatRoom / the auditorium join flow) get
      // registered optimistically via upsertRoom, before RC's own
      // subscriptions.get snapshot necessarily reflects the just-granted
      // membership yet. That race is most visible on a guest's very first
      // session, whose initial loadSubscriptions can still be in flight when
      // the auditorium join fires — a stale/incomplete snapshot landing right
      // after must not wipe the optimistic entry back out.
      for (const [id, r] of Object.entries(s.rooms)) {
        if (
          !rooms[id] &&
          (r.customFields?.nexgenMeetingRoom || r.customFields?.nexgenAuditoriumChat)
        ) {
          rooms[id] = r;
        }
      }
      const activeRoomId = s.activeRoomId && rooms[s.activeRoomId] ? s.activeRoomId : null;
      return {
        rooms,
        roomOrder: sortedOrder(rooms),
        totalUnread: fullTotalUnread(rooms),
        activeRoomId,
      };
    }),

  setGuestRestrictedRoomId: (roomId) => set({ guestRestrictedRoomId: roomId }),

  upsertRoom: (partial) =>
    set((s) => {
      if (s.guestRestrictedRoomId && partial._id === s.guestRestrictedRoomId) return s;
      // A hidden room touched again (new message → RC reopens it) comes back with its full
      // data and leaves the hidden list, so it isn't listed twice.
      const hidden = s.hiddenRooms[partial._id];
      const existing = s.rooms[partial._id] ?? hidden ?? ({} as RCRoom);
      // Never restore unread > 0 for the room the user is currently viewing
      // (unless they're scrolled up, where new messages genuinely are unread)
      const safePartial =
        partial._id === s.activeRoomId && !s.activeRoomScrolledUp && (partial.unread ?? 0) > 0
          ? { ...partial, unread: 0 }
          : partial;
      // Strip undefined fields so a partial update (e.g. subscriptions-changed without
      // lastMessage) doesn't erase defined fields on the existing room and corrupt sort order.
      const mergePartial = Object.fromEntries(
        Object.entries(safePartial).filter(([, v]) => v !== undefined)
      ) as typeof safePartial;
      const updated = { ...existing, ...mergePartial } as RCRoom;
      const rooms = { ...s.rooms, [partial._id]: updated };

      const oldKey = roomSortKey(existing);
      const newKey = roomSortKey(updated);
      // A room not yet listed (new, or unarchived/unhidden) is placed by its last message —
      // appending it would push an older room past the sidebar's per-section cap until reload.
      // One with no timestamp yet still sorts to the end, as before.
      const roomOrder =
        newKey !== oldKey || !s.roomOrder.includes(partial._id)
          ? insertInOrder(s.roomOrder, partial._id, newKey, rooms)
          : s.roomOrder;

      const unreadDelta = (updated.unread ?? 0) - (existing.unread ?? 0);
      const totalUnread = Math.max(0, s.totalUnread + unreadDelta);

      if (!hidden) return { rooms, roomOrder, totalUnread };
      const { [partial._id]: _unhidden, ...hiddenRooms } = s.hiddenRooms;
      return { rooms, roomOrder, totalUnread, hiddenRooms };
    }),

  removeRoom: (roomId) =>
    set((s) => {
      if (!s.rooms[roomId]) return s;
      const { [roomId]: _dropped, ...rooms } = s.rooms;
      const roomOrder = s.roomOrder.filter((id) => id !== roomId);
      const totalUnread = Math.max(0, s.totalUnread - (_dropped.unread ?? 0));
      const activeRoomId = s.activeRoomId === roomId ? null : s.activeRoomId;
      const { [roomId]: _droppedMembers, ...members } = s.members;
      return { rooms, roomOrder, totalUnread, activeRoomId, members };
    }),

  setActiveRoomScrolledUp: (v) => set({ activeRoomScrolledUp: v }),
  setJumpTo: (target) => set({ jumpTo: target }),

  setHiddenRooms: (list) => set({ hiddenRooms: Object.fromEntries(list.map((r) => [r._id, r])) }),

  hideRoom: (roomId) => {
    const room = get().rooms[roomId];
    if (!room) return;
    set((s) => ({ hiddenRooms: { ...s.hiddenRooms, [roomId]: room } }));
    get().removeRoom(roomId);
  },

  unhideRoom: (roomId) => {
    if (get().hiddenRooms[roomId]) get().upsertRoom({ _id: roomId });
  },

  deleteRoomForMe: (roomId) => {
    set((s) => {
      const deletedAt = { ...s.deletedAt, [roomId]: Date.now() };
      saveTsMap(DELETED_KEY, deletedAt);
      return { deletedAt };
    });
    get().hideRoom(roomId);
  },

  setRoomMembers: (roomId, members) =>
    set((s) => ({ members: { ...s.members, [roomId]: members } })),

  addRoomMember: (roomId, member) =>
    set((s) => {
      const existing = s.members[roomId];
      if (!existing || existing.some((m) => m._id === member._id)) return s;
      const room = s.rooms[roomId];
      const rooms = room
        ? {
            ...s.rooms,
            [roomId]: { ...room, usersCount: (room.usersCount ?? existing.length) + 1 },
          }
        : s.rooms;
      return { members: { ...s.members, [roomId]: [...existing, member] }, rooms };
    }),

  removeRoomMember: (roomId, userId) =>
    set((s) => {
      const existing = s.members[roomId];
      if (!existing || !existing.some((m) => m._id === userId)) return s;
      const room = s.rooms[roomId];
      const rooms = room
        ? {
            ...s.rooms,
            [roomId]: {
              ...room,
              usersCount: Math.max(0, (room.usersCount ?? existing.length) - 1),
            },
          }
        : s.rooms;
      return {
        members: { ...s.members, [roomId]: existing.filter((m) => m._id !== userId) },
        rooms,
      };
    }),

  invalidateRoomMembers: (roomId) =>
    set((s) => {
      if (!(roomId in s.members)) return s;
      const { [roomId]: _dropped, ...members } = s.members;
      return { members };
    }),

  setActiveRoom: (roomId) =>
    set((s) => {
      if (s.guestRestrictedRoomId && roomId === s.guestRestrictedRoomId) return s;
      const room = roomId ? s.rooms[roomId] : undefined;
      const unreadToClear = room?.unread ?? 0;
      const rooms =
        room && unreadToClear > 0 ? { ...s.rooms, [roomId!]: { ...room, unread: 0 } } : s.rooms;
      const totalUnread = Math.max(0, s.totalUnread - unreadToClear);
      const activeSidePanel = roomId !== s.activeRoomId ? null : s.activeSidePanel;
      // Opening a room closes the AI Assistant; clearing the room leaves it as it was.
      const aiActive = roomId ? false : s.aiActive;
      return { activeRoomId: roomId, rooms, totalUnread, activeSidePanel, aiActive };
    }),

  setAiActive: (active) =>
    set((s) =>
      active
        ? { aiActive: true, activeRoomId: null, activeSidePanel: null, showDirectory: false }
        : s.aiActive
          ? { aiActive: false }
          : s
    ),

  setSuppressRoomRestore: (v) => set({ suppressRoomRestore: v }),
  clearActiveRoom: () => set({ activeRoomId: null, aiActive: false, suppressRoomRestore: true }),

  prependMessages: (roomId, msgs) =>
    set((s) => ({
      messages: {
        ...s.messages,
        [roomId]: dedupById([...msgs, ...(s.messages[roomId] ?? [])]),
      },
    })),

  appendMessage: (roomId, msg) =>
    set((s) => {
      const existing = s.messages[roomId] ?? [];
      if (existing.some((m) => m._id === msg._id)) return s;
      return { messages: { ...s.messages, [roomId]: [...existing, msg] } };
    }),

  updateMessage: (roomId, msg) =>
    set((s) => {
      const existing = s.messages[roomId] ?? [];
      const prior = existing.find((m) => m._id === msg._id);
      return {
        messages: {
          ...s.messages,
          [roomId]: existing.map((m) => (m._id === msg._id ? mergeMessage(prior, msg) : m)),
        },
      };
    }),

  // Adds/removes exactly one emoji key for one user, computed off the LIVE store
  // state rather than a message snapshot handed in by the caller. Reacting twice
  // in quick succession (different emoji) each went through updateMessage's full
  // -object mergeMessage, and a later DDP echo for the first reaction (server
  // state as of that action, so missing the second) could wipe the second one
  // out via mergeMessage's reactions union treating "existing" as the frozen
  // snapshot at call time. Mutating the single key directly here means the
  // toggle can never regress to an older snapshot.
  toggleReaction: (roomId, messageId, emoji, username) =>
    set((s) => {
      const existing = s.messages[roomId] ?? [];
      return {
        messages: {
          ...s.messages,
          [roomId]: existing.map((m) => {
            if (m._id !== messageId) return m;
            const reactions = { ...(m.reactions ?? {}) };
            const slot = reactions[emoji];
            if (slot) {
              const names = slot.usernames ?? [];
              const newNames = names.includes(username)
                ? names.filter((u) => u !== username)
                : [...names, username];
              if (newNames.length === 0) delete reactions[emoji];
              else reactions[emoji] = { ...slot, usernames: newNames };
            } else {
              reactions[emoji] = { usernames: [username] };
            }
            return { ...m, reactions };
          }),
        },
      };
    }),

  replaceMessage: (roomId, oldId, newMsg) =>
    set((s) => {
      const existing = s.messages[roomId] ?? [];
      if (existing.some((m) => m._id === newMsg._id && m._id !== oldId)) {
        // A different message already occupies newMsg._id (DDP beat us via a
        // different flow) — just drop the optimistic stub.
        return {
          messages: { ...s.messages, [roomId]: existing.filter((m) => m._id !== oldId) },
        };
      }
      // Swap optimistic stub for real message in-place (no flash), merging in
      // any fields (e.g. attachments) the stub/a prior DDP echo already had
      // that this particular response doesn't carry.
      const prior = existing.find((m) => m._id === oldId);
      return {
        messages: {
          ...s.messages,
          [roomId]: existing.map((m) => (m._id === oldId ? mergeMessage(prior, newMsg) : m)),
        },
      };
    }),

  removeMessage: (roomId, msgId) =>
    set((s) => ({
      messages: {
        ...s.messages,
        [roomId]: (s.messages[roomId] ?? []).filter((m) => m._id !== msgId),
      },
    })),

  clearRoomMessages: (roomId) =>
    set((s) => {
      const clearedAt = { ...s.clearedAt, [roomId]: Date.now() };
      saveClearedAt(clearedAt);
      return { clearedAt };
    }),

  purgeRoomMessages: (roomId) =>
    set((s) => ({
      messages: { ...s.messages, [roomId]: [] },
      loadingMessages: { ...s.loadingMessages, [roomId]: false },
      hasMoreMessages: { ...s.hasMoreMessages, [roomId]: true },
    })),

  markRoomRead: (roomId) =>
    set((s) => {
      const room = s.rooms[roomId];
      if (!room || (room.unread === 0 && !room.tunread?.length)) return s;
      const rooms = {
        ...s.rooms,
        [roomId]: { ...room, unread: 0, tunread: [], threadPreview: undefined },
      };
      const totalUnread = Math.max(0, s.totalUnread - (room.unread ?? 0));
      return { rooms, totalUnread };
    }),

  markRoomUnread: (roomId) =>
    set((s) => {
      const room = s.rooms[roomId];
      if (!room || room.unread > 0) return s;
      const rooms = { ...s.rooms, [roomId]: { ...room, unread: 1 } };
      return { rooms, totalUnread: s.totalUnread + 1 };
    }),

  clearThreadUnread: (roomId, msgId) =>
    set((s) => {
      const room = s.rooms[roomId];
      if (!room?.tunread?.length || !room.tunread.includes(msgId)) return s;
      const tunread = room.tunread.filter((id) => id !== msgId);
      // Drop the stashed preview too once no unread thread remains to attribute it to.
      const threadPreview = tunread.length ? room.threadPreview : undefined;
      const rooms = { ...s.rooms, [roomId]: { ...room, tunread, threadPreview } };
      return { rooms };
    }),

  markAllRoomsRead: () =>
    set((s) => {
      const rooms = { ...s.rooms };
      let changed = false;
      for (const id of Object.keys(rooms)) {
        const room = rooms[id];
        // totalUnread never counts thread-only unreads, so don't gate this loop on it —
        // a room can have tunread/threadPreview to clear even while totalUnread is 0.
        if (room.unread || room.tunread?.length) {
          rooms[id] = { ...room, unread: 0, tunread: [], threadPreview: undefined };
          changed = true;
        }
      }
      if (!changed) return s;
      return { rooms, totalUnread: 0 };
    }),

  // Forces per-room unread to match a fresh server-truth snapshot (e.g. from the periodic
  // subscriptions.get refresh that also drives the toolbar badge), so a stale optimistic
  // increment/correction race can never leave the room list permanently out of sync with it.
  reconcileUnreadCounts: (counts) =>
    set((s) => {
      let changed = false;
      const rooms = { ...s.rooms };
      for (const [id, unread] of Object.entries(counts)) {
        const room = rooms[id];
        if (room && id !== s.activeRoomId && room.unread !== unread) {
          rooms[id] = { ...room, unread };
          changed = true;
        }
      }
      if (!changed) return s;
      return { rooms, totalUnread: fullTotalUnread(rooms) };
    }),

  toggleFavorite: (roomId, favorite) =>
    set((s) => {
      const room = s.rooms[roomId];
      if (!room) return s;
      return { rooms: { ...s.rooms, [roomId]: { ...room, f: favorite } } };
    }),

  setEditingMessage: (msg) => set({ editingMessage: msg }),
  setDeleteConfirm: (confirm) => set({ deleteConfirm: confirm }),
  setDeleteAllConfirmOpen: (open) => set({ deleteAllConfirmOpen: open }),
  setReplyingTo: (reply) => set({ replyingTo: reply }),
  setQuotingMessage: (quote) => set({ quotingMessage: quote }),
  setForwardingMessage: (fwd) => set({ forwardingMessage: fwd }),
  setActiveSidePanel: (panel) => set({ activeSidePanel: panel }),
  setKeyboardShortcutsOpen: (open) => set({ keyboardShortcutsOpen: open }),
  setFilePreview: (preview) => set({ filePreview: preview }),
  setActiveThread: (thread) => set({ activeThread: thread }),
  setCreateDiscussionContext: (ctx) => set({ createDiscussionContext: ctx }),
  bumpDiscussionsVersion: (parentRoomId) =>
    set((s) => ({
      discussionsVersion: {
        ...s.discussionsVersion,
        [parentRoomId]: (s.discussionsVersion[parentRoomId] || 0) + 1,
      },
    })),
  setShowDirectory: (show) => set({ showDirectory: show }),

  setPresence: (userId, status) =>
    set((s) => ({ presence: { ...s.presence, [userId]: status as RCPresenceStatus } })),

  setTyping: (roomId, username, isTyping) =>
    set((s) => {
      // Empty username is used as a signal to clear all typing users for the room
      if (!username) {
        if (!s.typingUsers[roomId]?.length) return s;
        const { [roomId]: _removed, ...rest } = s.typingUsers;
        return { typingUsers: rest };
      }
      const current = s.typingUsers[roomId] ?? [];
      const next = isTyping
        ? current.includes(username)
          ? current
          : [...current, username]
        : current.filter((u) => u !== username);
      if (next === current) return s;
      return { typingUsers: { ...s.typingUsers, [roomId]: next } };
    }),

  setPanelPrefs: (prefs) => set({ panelPrefs: prefs }),

  setDraft: (key, text) =>
    set((s) => {
      const drafts = text
        ? { ...s.drafts, [key]: text }
        : (() => {
            if (!s.drafts[key]) return s.drafts;
            const { [key]: _r, ...rest } = s.drafts;
            return rest;
          })();
      saveDrafts(drafts);
      return { drafts };
    }),

  clearDraft: (key) =>
    set((s) => {
      if (!s.drafts[key]) return s;
      const { [key]: _r, ...drafts } = s.drafts;
      saveDrafts(drafts);
      return { drafts };
    }),

  setFileDraft: (key, file) => set((s) => ({ fileDrafts: { ...s.fileDrafts, [key]: file } })),

  clearFileDraft: (key) =>
    set((s) => {
      if (!s.fileDrafts[key]) return s;
      const { [key]: _r, ...fileDrafts } = s.fileDrafts;
      return { fileDrafts };
    }),

  pushNotification: (n) => set({ inAppNotification: n }),
  clearNotification: () => set({ inAppNotification: null }),
  setCurrentUser: (u) => set({ currentUser: u }),

  loadRoomHistory: async (roomId, roomType, signal) => {
    const s = get();
    // Live DDP messages get appended to rooms that were never opened, so having
    // messages doesn't mean history was loaded — hasMoreMessages is only set by a load.
    if (s.loadingMessages[roomId]) return;
    if (s.messages[roomId]?.length && roomId in s.hasMoreMessages) return;
    set((s) => ({ loadingMessages: { ...s.loadingMessages, [roomId]: true } }));
    try {
      const msgs = await RocketChatRESTService.getRoomHistory(
        roomId,
        roomType as 'c' | 'p' | 'd',
        undefined,
        50,
        signal
      );
      if (signal?.aborted) return;
      set((s) => ({
        // Keep any live messages that arrived before the history did.
        messages: {
          ...s.messages,
          [roomId]: dedupById([...msgs].reverse().concat(s.messages[roomId] ?? [])),
        },
        hasMoreMessages: { ...s.hasMoreMessages, [roomId]: msgs.length >= 50 },
      }));
    } catch {
      // aborted or failed — loading flag is reset below either way
    } finally {
      // Also on abort: a stuck `true` would block every later load of this room.
      set((s) => ({ loadingMessages: { ...s.loadingMessages, [roomId]: false } }));
    }
  },

  loadMoreHistory: async (roomId) => {
    const s = get();
    const room = s.rooms[roomId];
    const msgs = s.messages[roomId];
    if (!room || s.loadingMessages[roomId] || s.hasMoreMessages[roomId] === false) return;
    if (!msgs?.length) return;
    // DDP messages carry ts as { $date } — normalise so the query isn't "[object Object]".
    const oldest = toSortKey(msgs[0]?.ts);
    if (!oldest) return;
    set((s) => ({ loadingMessages: { ...s.loadingMessages, [roomId]: true } }));
    try {
      const older = await RocketChatRESTService.getRoomHistory(
        roomId,
        room.t as 'c' | 'p' | 'd',
        oldest
      );
      if (older.length === 0) {
        set((s) => ({ hasMoreMessages: { ...s.hasMoreMessages, [roomId]: false } }));
      } else {
        get().prependMessages(roomId, [...older].reverse());
        if (older.length < 50) {
          set((s) => ({ hasMoreMessages: { ...s.hasMoreMessages, [roomId]: false } }));
        }
      }
    } catch {
    } finally {
      set((s) => ({ loadingMessages: { ...s.loadingMessages, [roomId]: false } }));
    }
  },
}));
